import { Spinner, Text } from "@primer/react";
import { useState } from "react";
import { MButton, FloatingButton } from "../../../components/mobile";
import { IClassListItem } from "../../../model/respone/classes/IClassListItem";
import { INhomLop } from "../../../model/respone/classes/INhomLop";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import { ISubjectGroup } from "../classesHelpers";
import ClassCard from "./ClassCard";
import FilterModal from "./FilterModal";
import SubjectCard from "./SubjectCard";

interface IRegisterTabProps {
    /** Chặn UI đăng ký khi /queue/status chưa trả snapshot thời gian (mirror desktop). */
    blockedForQueue: boolean;
    classesLoading: boolean;
    classesError: boolean;
    classesErrorMessage?: string;
    onRetryLoad: () => void;

    // ----- filter -----
    hinhThuc: number;
    nhomLop: string;
    nhomLopOptions: INhomLop[];
    onApplyFilter: (hinhThuc: number, nhomLop: string) => void;

    // ----- data -----
    filteredSubjects: ISubjectGroup[];
    filteredCombinedClasses: IClassListItem[];
    selectedSubjectKey: string | null;
    onSelectSubject: (subjectKey: string) => void;
    expandedSub: { type: "lth" | "sh"; classId: number } | null;
    onToggleExpand: (type: "lth" | "sh", classId: number) => void;

    // ----- registration state + handlers -----
    myList: IMyRegistration[];
    pendingClassIds: number[];
    confirmedClassIds: number[];
    gateReason?: string;
    onRegister: (classId: number) => void;
    onPickPair: (parentClassId: number, childClassId: number) => void;
    onRefreshSlots: () => void;
}

/**
 * Tab "Đăng ký" — gộp listSubject (B2+B3) và listDetailWithSubject (B4) của desktop.
 *  - nhomLop === ""  → danh sách thẻ học phần (accordion mở danh sách lớp).
 *  - nhomLop !== ""  → danh sách phẳng thẻ lớp tín chỉ theo nhóm lớp.
 */
const RegisterTab = ({
    blockedForQueue,
    classesLoading,
    classesError,
    classesErrorMessage,
    onRetryLoad,
    hinhThuc,
    nhomLop,
    nhomLopOptions,
    onApplyFilter,
    filteredSubjects,
    filteredCombinedClasses,
    selectedSubjectKey,
    onSelectSubject,
    expandedSub,
    onToggleExpand,
    myList,
    pendingClassIds,
    confirmedClassIds,
    gateReason,
    onRegister,
    onPickPair,
    onRefreshSlots,
}: IRegisterTabProps) => {
    const [filterOpen, setFilterOpen] = useState(false);

    // Số bộ lọc đang áp dụng → badge trên FAB.
    const activeFilterCount = (hinhThuc !== 0 ? 1 : 0) + (nhomLop !== "" ? 1 : 0);
    const byNhomLop = nhomLop !== "";

    if (blockedForQueue) {
        return (
            <div className="m-loading">
                <Spinner size="large" />
                <Text as="p" sx={{ mt: 2, color: "fg.muted", fontSize: 1 }}>
                    Đang tải thông tin thời gian đăng ký từ cổng. Vui lòng đợi...
                </Text>
            </div>
        );
    }

    return (
        <>
            {classesError && (
                <div className="m-banner m-banner-danger">
                    <div className="m-banner-row" style={{ flexWrap: "wrap" }}>
                        <span>
                            {classesErrorMessage?.trim() ||
                                "Không tải được danh sách lớp. Vui lòng thử lại sau giây lát."}
                        </span>
                        <MButton variant="danger" onClick={onRetryLoad}>
                            Tải lại
                        </MButton>
                    </div>
                </div>
            )}

            <div className="m-section-title">
                {byNhomLop
                    ? "Lớp tín chỉ theo nhóm lớp"
                    : "Học phần được đăng ký học"}
            </div>

            {(byNhomLop || selectedSubjectKey != null) && (
                <div style={{ marginBottom: 8, textAlign: "right" }}>
                    <MButton
                        variant="invisible"
                        leadingVisual={() => <i className="fas fa-sync-alt" />}
                        onClick={onRefreshSlots}
                    >
                        Tải lại số chỗ
                    </MButton>
                </div>
            )}

            {/* ----- Danh sách ----- */}
            {!byNhomLop ? (
                classesLoading && filteredSubjects.length === 0 ? (
                    <div className="m-loading">
                        <Spinner size="medium" />
                        <Text as="p" sx={{ mt: 2, color: "fg.muted" }}>Đang tải học phần...</Text>
                    </div>
                ) : filteredSubjects.length === 0 ? (
                    <div className="m-empty">
                        <i className="fas fa-book-open" />
                        Không có học phần nào.
                    </div>
                ) : (
                    filteredSubjects.map(s => (
                        <SubjectCard
                            key={s.subjectKey}
                            subject={s}
                            selected={selectedSubjectKey === s.subjectKey}
                            onSelect={onSelectSubject}
                            myList={myList}
                            pendingClassIds={pendingClassIds}
                            confirmedClassIds={confirmedClassIds}
                            gateReason={gateReason}
                            expandedSub={expandedSub}
                            onToggleExpand={onToggleExpand}
                            onRegister={onRegister}
                            onPickPair={onPickPair}
                        />
                    ))
                )
            ) : classesLoading && filteredCombinedClasses.length === 0 ? (
                <div className="m-loading">
                    <Spinner size="medium" />
                    <Text as="p" sx={{ mt: 2, color: "fg.muted" }}>Đang tải lớp...</Text>
                </div>
            ) : filteredCombinedClasses.length === 0 ? (
                <div className="m-empty">
                    <i className="fas fa-layer-group" />
                    Không có lớp nào trong nhóm này.
                </div>
            ) : (
                filteredCombinedClasses.map(c => (
                    <ClassCard
                        key={c.classId}
                        item={c}
                        showSubject
                        myList={myList}
                        pendingClassIds={pendingClassIds}
                        confirmedClassIds={confirmedClassIds}
                        gateReason={gateReason}
                        expanded={expandedSub?.classId === c.classId}
                        onToggleExpand={onToggleExpand}
                        onRegister={onRegister}
                        onPickPair={onPickPair}
                    />
                ))
            )}

            <Text as="p" sx={{ fontSize: 0, fontStyle: "italic", color: "fg.muted", mt: 2, mb: 2 }}>
                Lưu ý: Thẻ nền vàng là lớp trùng thời khóa biểu hoặc đã hết chỗ — không đăng ký được.
            </Text>

            {/* FAB mở bộ lọc */}
            <FloatingButton
                icon={<i className="fas fa-sliders" />}
                label="Bộ lọc"
                badge={activeFilterCount}
                ariaLabel="Mở bộ lọc môn học"
                onClick={() => setFilterOpen(true)}
            />

            <FilterModal
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                hinhThuc={hinhThuc}
                nhomLop={nhomLop}
                nhomLopOptions={nhomLopOptions}
                onApply={onApplyFilter}
            />
        </>
    );
};

export default RegisterTab;
